import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { Tooltip } from 'react-tooltip';
import { useGetCreditAssessmentHistory } from '../../../services/loanService';
import { format } from 'date-fns';
import { AssessmentRecommendation } from '../../../services/schemas/loanServiceSchemas';

interface CreditAssessmentItem {
    id: string;
    applicationId: string;
    applicationNumber: string;
    customerName: string;
    customerBusinessName?: string;
    type: string;
    status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED';
    recommendation: AssessmentRecommendation;
    riskLevel: 'LOW' | 'MEDIUM' | 'HIGH';
    approvedAmount: number;
    interestRate: number;
    termMonths: number;
    assessedBy: string;
    assessedDate: string;
    createdDate: string;
}

const CreditAssessment = () => {
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(20);
    const ALL_ASSESSMENTS_PAGE_SIZE = 10000;

    // Fetch credit assessment history
    const {
        data: creditAssessmentHistory,
        isLoading,
        error
    } = useGetCreditAssessmentHistory({
        page: 0,
        size: ALL_ASSESSMENTS_PAGE_SIZE
    });

    const columns = [
        {
            name: 'Application #',
            selector: (row: CreditAssessmentItem) => row.applicationNumber,
            sortable: true,
            cell: (row: CreditAssessmentItem) => (
                <Link to={`/credit-assessment/${row.applicationId}`} className="text-primary">
                    {row.applicationNumber}
                </Link>
            ),
        },
        {
            name: 'Customer',
            selector: (row: CreditAssessmentItem) => row.customerBusinessName || row.customerName,
            sortable: true,
            cell: (row: CreditAssessmentItem) => (
                <div>
                    {row.customerBusinessName ? (
                        <>
                            <div>{row.customerBusinessName}</div>
                            <small className="text-muted">{row.customerName}</small>
                        </>
                    ) : (
                        row.customerName
                    )}
                </div>
            ),
        },
        {
            name: 'Amount',
            selector: (row: CreditAssessmentItem) => row.approvedAmount,
            format: (row: CreditAssessmentItem) => row.approvedAmount.toLocaleString('en-ZA', {
                style: 'currency',
                currency: 'ZAR'
            }),
            sortable: true,
            right: true,
        },
        {
            name: 'Risk Level',
            selector: (row: CreditAssessmentItem) => row.riskLevel,
            cell: (row: CreditAssessmentItem) => (
                <span className={`badge bg-${
                    row.riskLevel === 'LOW' ? 'success' : 
                    row.riskLevel === 'MEDIUM' ? 'warning' : 'danger'
                }`}>
                    {row.riskLevel.charAt(0) + row.riskLevel.slice(1).toLowerCase()}
                </span>
            ),
            sortable: true,
        },
        {
            name: 'Status',
            selector: (row: CreditAssessmentItem) => row.status,
            cell: (row: CreditAssessmentItem) => (
                <span className={`badge bg-${
                    row.status === 'COMPLETED' ? 'success' : 
                    row.status === 'IN_PROGRESS' ? 'warning' : 'info'
                }`}>
                    {row.status.split('_').map(word =>
                        word.charAt(0) + word.slice(1).toLowerCase()
                    ).join(' ')}
                </span>
            ),
            sortable: true,
        },
        {
            name: 'Recommendation',
            selector: (row: CreditAssessmentItem) => row.recommendation,
            cell: (row: CreditAssessmentItem) => (
                <span className={`badge bg-${row.recommendation === 'APPROVE' ? 'success' : 'danger'}`}>
                    {row.recommendation.charAt(0) + row.recommendation.slice(1).toLowerCase()}
                </span>
            ),
            sortable: true,
        },
        {
            name: 'Assessed By',
            selector: (row: CreditAssessmentItem) => row.assessedBy,
            sortable: true,
        },
        {
            name: 'Assessment Date',
            selector: (row: CreditAssessmentItem) => row.assessedDate,
            cell: (row: CreditAssessmentItem) => format(new Date(row.assessedDate), 'dd MMM yyyy HH:mm'),
            sortable: true,
        }
    ];

    // Calculate statistics from the history data
    const stats = [
        {
            title: 'Total Assessments',
            value: (creditAssessmentHistory?.content?.length || 0).toString(),
            icon: 'fa-file-alt',
            color: 'primary'
        },
        {
            title: 'Pending Assessment',
            value: creditAssessmentHistory?.content?.filter(item => item.status === 'PENDING').length.toString() || '0',
            icon: 'fa-clock',
            color: 'warning'
        },
        {
            title: 'Approved',
            value: creditAssessmentHistory?.content?.filter(item => item.recommendation === 'APPROVE').length.toString() || '0',
            icon: 'fa-check-circle',
            color: 'success'
        },
        {
            title: 'Rejected',
            value: creditAssessmentHistory?.content?.filter(item => item.recommendation === AssessmentRecommendation.REJECT).length.toString() || '0',
            icon: 'fa-times-circle',
            color: 'danger'
        },
    ];

    const handlePageChange = (page: number) => {
        setPage(page);
    };

    const handlePerRowsChange = (newSize: number, page: number) => {
        setSize(newSize);
        setPage(page);
    };

    return (
        <div>
            <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
                <div className="container-fluid">
                    <div className="row g-3 mb-3 align-items-center">
                        <div className="col">
                            <ol className="breadcrumb bg-transparent mb-0">
                                <li className="breadcrumb-item"><Link className="text-secondary" to="/">Home</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">Credit Assessment</li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
                <div className="container-fluid">
                    {/* Statistics Cards */}
                    <div className="row g-3 mb-3 row-deck">
                        {stats.map((stat, index) => (
                            <div key={index} className="col-lg-3 col-md-6 col-sm-6">
                                <div className="card">
                                    <div className="card-body">
                                        <div className={`text-${stat.color}`}>
                                            <i className={`fa ${stat.icon} fa-lg`}></i>
                                            <span className="ms-2">{stat.title}</span>
                                        </div>
                                        <h3 className="mt-2 mb-0">{stat.value}</h3>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Credit Assessment History Table */}
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-header">
                                    <h6 className="card-title mb-0">Credit Assessment History</h6>
                                </div>
                                <div className="card-body">
                                    {error ? (
                                        <div className="alert alert-danger">
                                            Failed to load credit assessment history: {(error as Error).message}
                                        </div>
                                    ) : (
                                        <DataTable
                                            columns={columns}
                                            data={(creditAssessmentHistory?.content || []) as CreditAssessmentItem[]}
                                            progressPending={isLoading}
                                            pagination
                                            onChangeRowsPerPage={handlePerRowsChange}
                                            onChangePage={handlePageChange}
                                            paginationPerPage={size}
                                            paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                            dense
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreditAssessment;
